<div class="view-content">
  @if (!hasRequiredAnalysisContext) {
    <div class="analysis-unavailable-hint">
      <fa-icon [icon]="faExclamationCircle"></fa-icon>
      No active browser session or mobile device console.
      <wm-start-browser-session-action></wm-start-browser-session-action>
    </div>
  } @else if (mobileAnalysisUnavailableMessage) {
    <div class="analysis-unavailable-hint">
      <fa-icon [icon]="faExclamationCircle"></fa-icon>
      {{ mobileAnalysisUnavailableMessage }}
    </div>
  }
  <div class="analysis-content">
    <div class="analysis-controls">
      <button class="analysis-button" pButton icon="true" [disabled]="analyzing || !canStartAnalysis" [label]="isMobileAnalysis ? 'Start Mobile Analysis' : 'Start Webpage Analysis'" (click)="startAnalysis()">
        <fa-icon [icon]="faSparkles" class="icon"></fa-icon>
      </button>
      @if (!isMobileAnalysis) {
        <p-select
          class="language-select"
          [options]="languageOptions"
          [(ngModel)]="selectedLanguage"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          [disabled]="analyzing || !activeExpedition"
          pTooltip="Choose analysis language"
        ></p-select>
      }
    </div>

    @if (!isMobileAnalysis) {
      <div class="analysis-settings">
        <div class="analysis-mode">
          <p-selectButton
            [options]="modeOptions"
            [(ngModel)]="analysisMode"
            optionLabel="label"
            optionValue="value"
            [disabled]="analyzing || !activeExpedition"
            [ngModelOptions]="{ standalone: true }"
            >
            <ng-template pTemplate="item" let-opt>
              <fa-icon [icon]="opt.icon" class="icon"></fa-icon>
              <span>{{ opt.label }}</span>
            </ng-template>
          </p-selectButton>
        </div>

        @if (analysisMode === 'custom') {
          <div class="custom-filters">
            <div class="filter-row">
              <label for="levels">A11y Levels:</label>
              <p-multiSelect
                id="levels"
                name="selectedA11yLevels"
                [options]="a11yLevelOptions"
                [(ngModel)]="selectedA11yLevels"
                optionLabel="label"
                optionValue="value"
                placeholder="Select A11y Levels"
                [disabled]="analyzing || !activeExpedition"
                appendTo="body"
              ></p-multiSelect>
            </div>

            @if (hasAuditContext && activityOptions.length > 0) {
              <div class="filter-row">
                <label for="activities">Activities:</label>
                <p-multiSelect
                  id="activities"
                  name="selectedActivities"
                  [options]="activityOptions || []"
                  [(ngModel)]="selectedActivities"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select Activities"
                  appendTo="body"
                  [disabled]="analyzing || !activeExpedition"
                  [maxSelectedLabels]="1"
                  >
                </p-multiSelect>
              </div>
            }
          </div>
        }
      </div>
    }

    @if (!!analysisError) {
      <div class="error-container">
        <fa-icon icon="exclamation-triangle" class="error-icon"></fa-icon>
        <span class="error-message">{{ analysisError }}</span>
      </div>
    }

    @if (factSessionInfo && hasFactSessionProcesses()) {
      <wm-fact-session-observability
        [session]="factSessionInfo"
        (relatedFactSelected)="showRelatedFact($event)">
      </wm-fact-session-observability>
    }

    @if (analyzing && analysisProgress && !hasFactSessionProcesses()) {
      <div class="progress-container">
        <div class="progress-status">
          <div class="progress-info">
            <p-progressSpinner strokeWidth="6" ariaLabel="loading" animationDuration="1s" [style]="{ width: '20px', height: '20px' }"></p-progressSpinner>
            <span class="progress-message">{{ analysisProgress.message }}</span>
          </div>
          <span class="progress-percentage">{{ analysisProgress.percentage }}%</span>
        </div>
        <div class="progress-bar-wrapper">
          <p-progressBar
            [value]="analysisProgress.percentage"
            [showValue]="false"
            class="progress-bar">
          </p-progressBar>
        </div>
      </div>
    }

    @if (!!this.analyzing && this.stats?.totalObservations > 0 || !this.analyzing && !!this.stats) {
      <p-accordion class="observations" [value]="0">
        <div class="observation-header">
          @if (stats?.violation) {
            <div class="issue-stats">
              <div class="total-observations">{{ stats.totalObservations }} Observations</div>
              <div class="stat-pills">
                <div class="stat critical" pTooltip="Violation" tooltipPosition="top">{{ stats.violation.total }}</div>
                <div class="stat compliant" pTooltip="Compliant" tooltipPosition="top">{{ stats.passes.total }}</div>
                <div class="stat minor" pTooltip="Inapplicable" tooltipPosition="top">{{ stats.inapplicable.total }}</div>
              </div>
            </div>
          }
          @if (canUseAuditFilters) {
          <div class="filter">
            <div class="filter-item">
              <p-checkbox inputId="filter2" (onChange)="updateFilter($event.checked)" [binary]="true" [(ngModel)]="filterObservationsForAuditActivity"></p-checkbox>
              <label for="filter2">Filter for audit activity</label>
            </div>
          </div>
        }
          <wm-add-finding-action
            class="add-all-observations-button"
            quantifier="all"
            [pending]="bulkAddPending.all"
            [disabled]="!hasAnyNotYetAddedObservations()"
            (addToActive)="addAllObservationsToFindings()"
            (addToNewManualTest)="startManualTestAndAddAllObservationsToFindings()">
          </wm-add-finding-action>
        </div>

        <!-- Violations Tab -->
        @if (observations?.violations) {
        <p-accordion-panel [value]="0">
          <p-accordion-header>
            <span class="result-header">
              <span class="header-text">
                Violations
              </span>
              <fa-icon class="violation" [icon]="faTimesCircle"/>
            </span>
          </p-accordion-header>
          <p-accordion-content>
            @if (observations?.violations) {
              <div>
                @if (!!stats?.violation) {
                  <div class="issue-stats">
                    <div class="total">{{ stats.violation.total }} Observations</div>
                    <div class="stat-pills">
                      <div class="stat critical" pTooltip="Critical" tooltipPosition="top">{{ stats.violation.critical }}</div>
                      <div class="stat serious" pTooltip="Serious" tooltipPosition="top">{{ stats.violation.serious }}</div>
                      <div class="stat moderate" pTooltip="Moderate" tooltipPosition="top">{{ stats.violation.moderate }}</div>
                      <div class="stat minor" pTooltip="Minor" tooltipPosition="top">{{ stats.violation.minor }}</div>
                    </div>
                  </div>
                }
                <div class="observation-count">
                  {{
                    filteredObservations.violations.length | i18nPlural: {
                      '=0': 'No violations',
                      '=1': '1 violation',
                      'other': '# violations'
                    }
                  }}
                  ({{ observations.violations.length - filteredObservations.violations.length }} filtered)
                </div>
                <wm-add-finding-action
                  class="add-all-button"
                  quantifier="all"
                  [pending]="bulkAddPending.violations"
                  [disabled]="!hasNotYetAddedObservations('violations')"
                  (addToActive)="addAllToFindings('violations')"
                  (addToNewManualTest)="startManualTestAndAddAllToFindings('violations')">
                </wm-add-finding-action>
                <wm-a11y-observation-details
                  [rules]="filteredObservations.violations"
                  [activeTarget]="activeTarget"
                  [activeElement]="activeElement"
                  [isViolation]=true
                  [activeAuditId]="canShowRelatedTasks ? activeAuditId : undefined"
                  [activeObservationAssessmentTasks]="canShowRelatedTasks ? activeObservationAssessmentTasks : undefined"
                  [auditInfo]="canShowRelatedTasks ? auditInfo : undefined"
                  [activeElementStateInfos]="activeElementStateInfos"
                  [supportsInteractiveDomHighlight]="supportsInteractiveDomHighlight()"
                  [activeTargetFailureSummary]="activeTargetFailureSummary"
                  [addFindingPending]="addFindingPending"
                  [isObservationAdded]="isObservationAdded"
                  (addToActive)="addFinding()"
                  (addToAllRelatedTasks)="addFindingForRelatedTasks($event)"
                  (addToNewManualTest)="startManualTestAndAddFinding()"
                  (clearObservation)="clearObservationSelection()"
                  (showObservation)="showObservation($event.rule, $event.node, $event.target)">
                </wm-a11y-observation-details>
              </div>
            }
          </p-accordion-content>
        </p-accordion-panel>
        }

        <!-- Compliant Tab -->
        @if (observations?.passes) {
          <p-accordion-panel [value]="1">
            <p-accordion-header>
              <span class="result-header">
                <span class="header-text">
                  Compliant
                </span>
                <fa-icon class="compliant" [icon]="faCheckCircle"/>
              </span>
            </p-accordion-header>
            <p-accordion-content>

              <div class="issue-stats">
                <div class="total">{{ observations.passes.length }} Observations</div>
              </div>
              <div class="observation-count">
                {{
                  filteredObservations.passes.length | i18nPlural: {
                    '=0': 'No compliant',
                    '=1': '1 compliant',
                    'other': '# compliant'
                  }
                }}
                ({{ observations.passes.length - filteredObservations.passes.length }} filtered)
              </div>
              <wm-add-finding-action
                class="add-all-button"
                quantifier="all"
                [pending]="bulkAddPending.passes"
                [disabled]="!hasNotYetAddedObservations('passes')"
                (addToActive)="addAllToFindings('passes')"
                (addToNewManualTest)="startManualTestAndAddAllToFindings('passes')">
              </wm-add-finding-action>
              <wm-a11y-observation-details
                [rules]="filteredObservations.passes"
                [isViolation]=false
                [activeTarget]="activeTarget"
                [activeTargetFailureSummary]="activeTargetFailureSummary"
                [activeElement]="activeElement"
                [activeElementStateInfos]="activeElementStateInfos"
                [supportsInteractiveDomHighlight]="supportsInteractiveDomHighlight()"
                [activeAuditId]="canShowRelatedTasks ? activeAuditId : undefined"
                [activeObservationAssessmentTasks]="canShowRelatedTasks ? activeObservationAssessmentTasks : undefined"
                [auditInfo]="canShowRelatedTasks ? auditInfo : undefined"
                [addFindingPending]="addFindingPending"
                [isObservationAdded]="isObservationAdded"
                (addToActive)="addFinding()"
                (addToAllRelatedTasks)="addFindingForRelatedTasks($event)"
                (addToNewManualTest)="startManualTestAndAddFinding()"
                (clearObservation)="clearObservationSelection()"
                (showObservation)="showObservation($event.rule, $event.node, $event.target)">
              </wm-a11y-observation-details>
            </p-accordion-content>
          </p-accordion-panel>
        }

        <!-- Inapplicable Tab -->
        @if (observations?.inapplicable) {
          <p-accordion-panel [value]="2">
            <p-accordion-header>
            <span class="result-header">
              <span class="header-text">
                Inapplicable
              </span>
              <fa-icon class="inapplicable" [icon]="faMinusCircle"/>
            </span>
            </p-accordion-header>
            <p-accordion-content>
              @if (observations?.inapplicable) {
                <div>
                  <div class="issue-stats">
                    <div class="total">{{ observations.inapplicable.length }} Observations</div>
                  </div>
                  <div class="observation-count">
                    {{
                      filteredObservations.inapplicable.length | i18nPlural: {
                        '=0': 'No inapplicable',
                        '=1': '1 inapplicable',
                        'other': '# inapplicable'
                      }
                    }}
                    ({{ observations.inapplicable.length - filteredObservations.inapplicable.length }} filtered)
                  </div>
                  <wm-add-finding-action
                    class="add-all-button"
                    quantifier="all"
                    [pending]="bulkAddPending.inapplicable"
                    [disabled]="!hasNotYetAddedObservations('inapplicable')"
                    (addToActive)="addAllToFindings('inapplicable')"
                    (addToNewManualTest)="startManualTestAndAddAllToFindings('inapplicable')">
                  </wm-add-finding-action>
                  <wm-a11y-observation-details
                          [rules]="filteredObservations.inapplicable"
                          [inapplicable]="true"
                          [isViolation]=false
                          [activeAuditId]="canShowRelatedTasks ? activeAuditId : undefined"
                          [auditInfo]="canShowRelatedTasks ? auditInfo : undefined"
                          [addFindingPending]="addFindingPending"
                          [isObservationAdded]="isObservationAdded"
                          [supportsInteractiveDomHighlight]="supportsInteractiveDomHighlight()"
                          (addToActive)="addFinding()"
                          (addToAllRelatedTasks)="addFindingForRelatedTasks($event)"
                          (addToNewManualTest)="startManualTestAndAddFinding()"
                          (clearObservation)="clearObservationSelection()"
                          (showObservation)="showObservation($event.rule, $event.node, $event.target)">
                  </wm-a11y-observation-details>
                </div>
              }
            </p-accordion-content>
          </p-accordion-panel>
        }
      </p-accordion>
    }

  </div>
</div>
